import moment from 'moment';

import {
  SupportMessageDirection,
  type ModelSupportChat,
  type ModelSupportMessageFile,
  type ModelSupportParticipant,
} from '@app/api/Support';
import { formatBytes } from '@app/utils/formatBytes';

import type {
  SupportChatMessageFileViewModel,
  SupportChatMessageViewModel,
  SupportParticipantsMap,
} from '@features/SupportChat/types';

const SUPPORT_FALLBACK_NAME = 'Техническая поддержка';
const OWN_FALLBACK_NAME = 'Вы';

const getParticipantName = (
  isOwn: boolean,
  participant?: ModelSupportParticipant
) => {
  const name = [participant?.NameFirst, participant?.NameLast]
    .filter(Boolean)
    .join(' ');

  if (name) return name;

  return isOwn ? OWN_FALLBACK_NAME : SUPPORT_FALLBACK_NAME;
};

const formatMessageTime = (timestamp: string) => {
  const date = moment(timestamp);

  return date.isSame(moment(), 'day')
    ? date.format('HH:mm')
    : date.format('DD.MM.YYYY HH:mm');
};

// Name по контракту уже с расширением; если бэкенд его не прислал —
// собираем имя из Id и Extension, чтобы файл было чем назвать при скачивании.
const getFileName = ({ Id, Name, Extension }: ModelSupportMessageFile) => {
  if (Name) return Name;

  return Extension ? `file-${Id}.${Extension}` : `file-${Id}`;
};

const mapSupportMessageFile = (
  file: ModelSupportMessageFile
): SupportChatMessageFileViewModel => ({
  id: file.Id,
  name: getFileName(file),
  size: file.Size ? formatBytes(file.Size) : null,
});

export const mapSupportMessages = (
  chat?: ModelSupportChat
): SupportChatMessageViewModel[] => {
  const participants: SupportParticipantsMap = new Map();

  // Участники берутся как есть, без склейки данных. При повторе ID
  // используется первая запись — как при поиске через Array.find.
  chat?.Participants?.forEach((participant) => {
    const { ParticipantId } = participant;

    if (ParticipantId && !participants.has(ParticipantId)) {
      participants.set(ParticipantId, participant);
    }
  });

  return (chat?.Messages ?? [])
    .slice()
    .sort((left, right) => moment(left.Timestamp).diff(right.Timestamp))
    .map((message) => {
      const participant = message.ParticipantId
        ? participants.get(message.ParticipantId)
        : undefined;

      const isOwn = message.Direction === SupportMessageDirection.In;

      return {
        id: message.Id,
        text: message.Message ?? '',
        time: formatMessageTime(message.Timestamp),
        isOwn,
        senderName: getParticipantName(isOwn, participant),
        nameFirst: participant?.NameFirst,
        nameLast: participant?.NameLast,
        avatar: participant?.Avatar,
        files: (message.Files ?? []).map(mapSupportMessageFile),
      };
    });
};
